import { useState, useEffect, useMemo, useCallback, useRef, type MouseEvent } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { Puck, type Data } from "@puckeditor/core";
import "@puckeditor/core/puck.css";
import axe, { type AxeNodeResult, type AxeResults, type Result as AxeResult } from "axe-core";
import {
  analyzeOptimization,
  buildEditorMoodVariables,
  extractThreeSecondTestInput,
  isMoodId,
  sanitizeSiteTheme,
  SITE_THEME_PRESETS,
  type MoodId,
  type OptimizationApplyResult,
  type PuckDiffOp,
  type ThreeSecondTestInput,
  type ThreeSecondTestResult,
} from "@ease/page-builder";
import { renderToHtml as renderPublishedHtml } from "@ease/page-builder/renderer";
import { getAffectedComponentIds } from "@ease/page-builder/collaboration";
import {
  usePageDetail,
  useAutoSave,
  createPage,
  createPagePreview,
  fetchPageDetail,
  publishPage,
  savePage,
  schedulePagePublish,
  cancelScheduledPagePublish,
  updatePageMetadata,
  updatePageSettings,
  type PublishAccessibilityAuditResult,
  type PageVersionSummary,
} from "@/hooks/use-pages";
import { applyOptimizationResultToEditorData, buildHeatmapLookup } from "@/hooks/use-design-copilot";
import { Check, Loader2, AlertCircle, Undo2, Redo2, Monitor, Tablet, Smartphone, ClipboardCheck, Search, Save, MessageSquare, Layers3, Sparkles, History, ExternalLink, Clock3, X, Keyboard, BarChart3, Lock, PanelRightClose, PanelRightOpen, Pin } from "lucide-react";
import DesignCopilot from "@/pages/page-builder/design-copilot";
import PageAnalyticsPanel from "@/components/pages/page-analytics-panel";
import { CollaborationBar } from "@/components/pages/collaboration-bar";
import { CollabCursors } from "@/components/pages/collab-cursors";
import { ConflictToast, type ConflictToastState } from "@/components/pages/conflict-toast";
import { describeCollabDiff, createCollabDiff, applyCollabDiffToData } from "@/components/pages/collab-diff";
import { DynamicContentPanel, type DynamicPreviewPreset } from "@/components/pages/dynamic-content-panel";
import SeoEditorPanel from "@/components/pages/seo-panel";
import StyleStateInjector from "@/components/pages/style-state-injector";
import { StylePanel } from "@/components/pages/style-panel";
import { StylePanelProvider } from "@/components/pages/style-panel-context";
import { LayerNavigatorPanel } from "@/components/pages/layer-navigator-panel";
import { SectionPicker } from "@/components/pages/section-picker";
import { PageCommentPanel } from "@/components/pages/page-comment-panel";
import { MoodPicker } from "@/components/pages/dss/mood-picker";
import { ConsistencyChecker } from "@/components/pages/dss/consistency-checker";
import { StylePromptGenerator } from "@/components/pages/dss/style-prompt-generator";
import { LiveTokenPreview } from "@/components/pages/dss/live-token-preview";
import { ThreeSecondTestPanel } from "@/components/pages/three-second-test-panel";
import { SimplifiedEditor } from "@/components/pages/simplified-editor";
import { VersionHistoryPanel } from "@/components/page-builder/version-history-panel";
import { ShortcutsOverlay } from "@/components/page-builder/shortcuts-overlay";
import { maybeFireFirstPublishConfetti } from "@/components/page-builder/first-publish-confetti";
import { createSavedSection, recordSavedSectionUse, type SavedSectionPuckData } from "@/hooks/use-saved-sections";
import { usePageCollab } from "@/hooks/use-page-collab";
import { usePageComments } from "@/hooks/use-page-comments";
import { duplicatePage } from "@/hooks/use-pages";
import { useResponsiveControls } from "@/hooks/use-responsive-controls";
import type { PuckComponent, SeoMeta } from "@/lib/seo-engine";
import type { SectionInsertTemplate } from "@/components/pages/section-picker";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useSiteTheme } from "@/hooks/use-site-theme";
import {
  INITIAL_DATA,
  normalizePageData,
  buildSavedSectionPayloadForSelection,
  cloneSavedSectionForInsertion,
  mergeZones,
  heatmapTone,
  normalizeShellType,
  normalizeEditorMode,
  normalizeEditorEntry,
  moveSelectedBlock,
  readShellData,
  removeSelectedSubtree,
  duplicateSelectedSubtree,
  insertSavedSectionNearSelection,
  type EditorMode,
  type SiteShellRecord,
} from "./editor-utils";
import {
  DYNAMIC_PREVIEW_CONTEXTS,
  EditorCommentPreviewContext,
  EditorDynamicPreviewContext,
  EditorCollabPreviewContext,
  EditorHeatmapContext,
} from "./editor-preview";
import { getPuckConfigForPageType } from "./editor-puck-config";
import {
  buildEditorCanvasThemeStyle,
  buildEditorThemeScopeCss,
  EditorThemeButtonIcon,
  EditorThemeButtonLabel,
  EditorThemePresetPicker,
} from "./editor-theme";

const SCHEDULE_MIN_LEAD_MS = 60 * 60 * 1000;
const SCHEDULE_TIMEZONE_FALLBACK = "UTC";

function normalizeSeoSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeSeoStateForPersist(meta: SeoMeta): SeoMeta {
  return {
    ...meta,
    slug: normalizeSeoSlug(meta.slug) || meta.slug,
  };
}

function buildSeoMetaPayload(meta: SeoMeta): Record<string, unknown> {
  const normalized = normalizeSeoStateForPersist(meta);
  const payload: Record<string, unknown> = { ...normalized };

  for (const key of ["ogImage", "canonicalUrl", "robots", "twitterCard", "ogType"]) {
    if (typeof payload[key] === "string" && payload[key].trim().length === 0) {
      delete payload[key];
    }
  }

  return payload;
}

function readBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || SCHEDULE_TIMEZONE_FALLBACK;
}

function readDateTimeLocalParts(value: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };
}

function readTimeZoneParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    calendar: "iso8601",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(date);

  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: Number(lookup.hour === "24" ? "0" : lookup.hour),
    minute: Number(lookup.minute),
  };
}

function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const parts = readTimeZoneParts(date, timeZone);
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute) - date.getTime();
}

function dateTimeLocalToUtc(value: string, timeZone: string): Date | null {
  const parts = readDateTimeLocalParts(value);
  if (!parts) {
    return null;
  }

  const localAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  let zonedDate = new Date(localAsUtc - getTimeZoneOffsetMs(timeZone, new Date(localAsUtc)));
  zonedDate = new Date(localAsUtc - getTimeZoneOffsetMs(timeZone, zonedDate));
  return zonedDate;
}

function formatDateTimeLocalForTimeZone(date: Date, timeZone: string): string {
  const parts = readTimeZoneParts(date, timeZone);
  return [
    String(parts.year).padStart(4, "0"),
    String(parts.month).padStart(2, "0"),
    String(parts.day).padStart(2, "0"),
  ].join("-") + `T${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

function formatScheduleTime(isoValue: string | null | undefined, timeZone: string): string {
  if (!isoValue) {
    return "No scheduled publish";
  }

  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) {
    return "Invalid scheduled publish time";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(date);
}

function formatScheduleCountdown(isoValue: string | null | undefined, nowMs: number): string | null {
  if (!isoValue) {
    return null;
  }

  const publishAtMs = new Date(isoValue).getTime();
  if (Number.isNaN(publishAtMs)) {
    return null;
  }

  const remainingMinutes = Math.ceil((publishAtMs - nowMs) / 60_000);
  if (remainingMinutes <= 0) {
    return "Publishing any minute";
  }

  if (remainingMinutes < 60) {
    return `${remainingMinutes} min until publish`;
  }

  const remainingHours = Math.ceil(remainingMinutes / 60);
  if (remainingHours < 48) {
    return `${remainingHours} hr until publish`;
  }

  return `${Math.ceil(remainingHours / 24)} days until publish`;
}

function buildScheduleTimeZoneOptions(selectedTimeZone: string): string[] {
  return Array.from(new Set([
    selectedTimeZone,
    SCHEDULE_TIMEZONE_FALLBACK,
    "Europe/Bucharest",
    "Europe/London",
    "America/New_York",
    "America/Los_Angeles",
  ]));
}

const SAVED_SECTION_CATEGORIES = [
  { value: "header", label: "Header / hero" },
  { value: "cta", label: "Call to action" },
  { value: "testimonials", label: "Testimonials" },
  { value: "pricing", label: "Pricing" },
  { value: "faq", label: "FAQ" },
  { value: "other", label: "Other" },
] as const;

type SavedSectionCategory = (typeof SAVED_SECTION_CATEGORIES)[number]["value"];

const SAVED_SECTION_THUMBNAILS: Record<SavedSectionCategory, string> = {
  header: "linear-gradient(135deg, var(--color-primary) 0%, #0a2540 100%)",
  cta: "linear-gradient(135deg, #f97316 0%, var(--color-primary) 100%)",
  testimonials: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
  pricing: "linear-gradient(135deg, #10b981 0%, #2563eb 100%)",
  faq: "linear-gradient(135deg, #14b8a6 0%, #0f172a 100%)",
  other: "linear-gradient(135deg, var(--color-card) 0%, var(--color-muted) 100%)",
};

function inferSavedSectionCategory(componentType: string | null | undefined): SavedSectionCategory {
  const normalized = componentType?.toLowerCase() ?? "";
  if (normalized.includes("hero") || normalized.includes("header")) return "header";
  if (normalized.includes("testimonial") || normalized.includes("review")) return "testimonials";
  if (normalized.includes("pricing") || normalized.includes("price")) return "pricing";
  if (normalized.includes("faq") || normalized.includes("question")) return "faq";
  if (normalized.includes("cta") || normalized.includes("button") || normalized.includes("contact")) return "cta";
  return "other";
}

function buildGeneratedSectionThumbnail(category: SavedSectionCategory): string {
  return SAVED_SECTION_THUMBNAILS[category];
}

function findComponentTypeById(nextData: Data, componentId: string): string | null {
  return readEditorConsistencyComponents(nextData)
    .find((component) => component.id === componentId)
    ?.type ?? null;
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable
    || target.closest("[contenteditable='true']") !== null
    || target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement;
}

function formatThreeSecondTestError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return "3-second test is rate limited right now. Try again in a moment.";
    }

    if (error.status === 503) {
      return "3-second test is temporarily unavailable right now.";
    }

    if (error.status >= 500) {
      return "3-second test hit a server error. Try again later.";
    }

    return error.message;
  }

  return error instanceof Error ? error.message : "3-second test failed.";
}

function readEditorConsistencyComponents(data: Data): Array<{
  id: string;
  type: string;
  props: Record<string, unknown>;
}> {
  const items = [
    ...(Array.isArray(data.content) ? data.content : []),
    ...Object.values(
      data.zones && typeof data.zones === "object"
        ? data.zones
        : {},
    ).flatMap((zoneItems) => (Array.isArray(zoneItems) ? zoneItems : [])),
  ];

  return items.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const component = item as { type?: unknown; props?: unknown };
    if (!component.props || typeof component.props !== "object") {
      return [];
    }

    const props = component.props as Record<string, unknown>;
    const id = typeof props.id === "string" ? props.id : null;
    if (!id) {
      return [];
    }

    return [{
      id,
      type: typeof component.type === "string" ? component.type : "unknown",
      props,
    }];
  });
}

function applyConsistencyFixesToEditorData(
  currentData: Data,
  fixes: ReadonlyArray<{ componentId: string; prop: string; value: unknown }>,
): Data {
  if (fixes.length === 0) {
    return currentData;
  }

  const fixMap = new Map<string, Map<string, unknown>>();
  for (const fix of fixes) {
    const componentFixes = fixMap.get(fix.componentId) ?? new Map<string, unknown>();
    componentFixes.set(fix.prop, fix.value);
    fixMap.set(fix.componentId, componentFixes);
  }

  const mapBlocks = (blocks: unknown): unknown => {
    if (!Array.isArray(blocks)) {
      return blocks;
    }

    return blocks.map((item) => {
      if (!item || typeof item !== "object") {
        return item;
      }

      const block = item as { props?: unknown };
      if (!block.props || typeof block.props !== "object") {
        return item;
      }

      const props = block.props as Record<string, unknown>;
      const componentId = typeof props.id === "string" ? props.id : null;
      const componentFixes = componentId ? fixMap.get(componentId) : undefined;

      if (!componentFixes) {
        return {
          ...item,
          props: { ...props },
        };
      }

      return {
        ...item,
        props: {
          ...props,
          ...Object.fromEntries(componentFixes),
        },
      };
    });
  };

  return {
    ...currentData,
    content: mapBlocks(currentData.content) as Data["content"],
    zones: currentData.zones && typeof currentData.zones === "object"
      ? Object.fromEntries(
        Object.entries(currentData.zones).map(([zoneKey, zoneItems]) => [zoneKey, mapBlocks(zoneItems)]),
      ) as Data["zones"]
      : currentData.zones,
  };
}

function readTokenPreviewValues(style: Record<string, string | number | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(style).flatMap(([key, value]) => (
      key.startsWith("--") && typeof value === "string"
        ? [[key, value] as const]
        : []
    )),
  );
}

const MAX_A11Y_ISSUES = 12;

function summarizeAuditImpact(
  issues: AxeResult[],
): PublishAccessibilityAuditResult["summary"] {
  return issues.reduce<PublishAccessibilityAuditResult["summary"]>((summary, issue) => {
    summary.violationCount += 1;
    if (issue.impact === "critical") summary.criticalCount += 1;
    if (issue.impact === "serious") summary.seriousCount += 1;
    if (issue.impact === "moderate") summary.moderateCount += 1;
    if (issue.impact === "minor") summary.minorCount += 1;
    return summary;
  }, {
    violationCount: 0,
    criticalCount: 0,
    seriousCount: 0,
    moderateCount: 0,
    minorCount: 0,
  });
}

function normalizeAccessibilityAudit(
  results: AxeResults,
): PublishAccessibilityAuditResult {
  const summary = summarizeAuditImpact(results.violations);
  return {
    source: "axe-core",
    auditedAt: new Date().toISOString(),
    status: summary.criticalCount > 0 ? "block" : summary.violationCount > 0 ? "warn" : "pass",
    summary,
    issues: results.violations.slice(0, MAX_A11Y_ISSUES).map((issue) => ({
      id: issue.id,
      impact: issue.impact ?? null,
      help: issue.help,
      helpUrl: issue.helpUrl,
      targets: issue.nodes.flatMap((node: AxeNodeResult) => node.target.map((target: unknown) => String(target))).slice(0, 25),
      nodeCount: issue.nodes.length,
      summary: issue.nodes[0]?.failureSummary ?? null,
    })),
  };
}

export default function PageEditorPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<Data>(normalizePageData(INITIAL_DATA));
  const dataRef = useRef<Data>(normalizePageData(INITIAL_DATA));
  const [currentPageId, setCurrentPageId] = useState<string | undefined>(pageId && pageId !== "new" ? pageId : undefined);
  const [shellSite, setShellSite] = useState<SiteShellRecord | null>(null);
  const [isLoadingShell, setIsLoadingShell] = useState(false);
  const [shellError, setShellError] = useState<string | null>(null);
  const [shellSaveStatus, setShellSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [shellSaveMessage, setShellSaveMessage] = useState<string | null>(null);
  const [builderError, setBuilderError] = useState<string | null>(null);
  const draftCreatingRef = useRef(false);
  const isNew = pageId === "new";
  const requestedSiteId = searchParams.get("siteId") ?? undefined;
  const shellType = normalizeShellType(searchParams.get("shell"));
  const requestedEditorMode = normalizeEditorMode(searchParams.get("mode"));
  const editorEntry = normalizeEditorEntry(searchParams.get("entry"));
  const isShellEditor = Boolean(requestedSiteId && shellType);
  const shellField = shellType === "header" ? "headerContent" : shellType === "footer" ? "footerContent" : null;
  const backHref = isShellEditor && requestedSiteId
    ? `/pages/settings?siteId=${requestedSiteId}`
    : "/pages/sites";
  const [editorMode, setEditorMode] = useState<EditorMode>(
    isShellEditor ? "full" : requestedEditorMode ?? "full",
  );
  const isSimpleEditor = !isShellEditor && editorMode === "simple";
  const showSidebar = !isSimpleEditor;
  const [showLayersPanel, setShowLayersPanel] = useState(!isSimpleEditor);
  const [isLayersCollapsed, setIsLayersCollapsed] = useState(false);
  const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(false);
  const [showShortcutsOverlay, setShowShortcutsOverlay] = useState(false);
  const [showThreeSecondTestPanel, setShowThreeSecondTestPanel] = useState(false);
  const [isRunningThreeSecondTest, setIsRunningThreeSecondTest] = useState(false);
  const [threeSecondTestInput, setThreeSecondTestInput] = useState<ThreeSecondTestInput | null>(null);
  const [threeSecondTestResult, setThreeSecondTestResult] = useState<ThreeSecondTestResult | null>(null);
  const [threeSecondTestError, setThreeSecondTestError] = useState<string | null>(null);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const [componentClipboard, setComponentClipboard] = useState<SavedSectionPuckData | null>(null);
  const [themeSaveState, setThemeSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [themeSaveMessage, setThemeSaveMessage] = useState<string | null>(null);
  const [pageMood, setPageMood] = useState<MoodId | null>(null);
  const [tokenPreviewOverrides, setTokenPreviewOverrides] = useState<Record<string, string>>({});

  // C14: Device preview
  const {
    activeBreakpoint: previewDevice,
    previewWidth: responsivePreviewWidth,
    setBreakpoint: setPreviewDevice,
  } = useResponsiveControls("desktop");
  const previewWidth = previewDevice === "desktop" ? "100%" : `${responsivePreviewWidth}px`;

  // C14: Undo/redo history
  const [history, setHistory] = useState<Data[]>([normalizePageData(INITIAL_DATA)]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyIndexRef = useRef(0);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const remoteApplyRef = useRef(false);
  const [collabConflict, setCollabConflict] = useState<ConflictToastState | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  const MAX_HISTORY = 50;
  const editorThemeScopeClass = "editor-theme-scope";
  const editorThemeScopeCss = useMemo(() => buildEditorThemeScopeCss(editorThemeScopeClass), []);

  // CR-02: Auto-save new pages as draft on first edit so collab/comments/auto-save
  // have a real pageId instead of "new". Prevents wasted API calls to /pages/new/*.
  const ensureDraftExists = useCallback(async (content: Data) => {
    if (!isNew || currentPageId || draftCreatingRef.current || isShellEditor) return;
    draftCreatingRef.current = true;
    try {
      const draft = await createPage({
        title: "Untitled Page",
        siteId: requestedSiteId,
        content,
        settings: pageMood ? { mood: pageMood } : undefined,
      });
      setCurrentPageId(draft.id);
      navigate(`/pages/editor/${draft.id}${window.location.search}`, { replace: true });
    } catch {
      // Non-fatal — user can still work, page will be created on publish
    } finally {
      draftCreatingRef.current = false;
    }
  }, [currentPageId, isNew, isShellEditor, navigate, pageMood, requestedSiteId]);

  const {
    comments: pageComments,
    isLoading: isLoadingComments,
    error: pageCommentsError,
    createComment,
    markResolved,
    replyToComment,
    refresh: refreshPageComments,
  } = usePageComments(currentPageId);

  const {
    collaborators,
    currentUser,
    isConnected,
    connectError,
    sendCursor,
    sendDiff,
    sendCommentEvent,
  } = usePageCollab(currentPageId, {
    onRemoteDiff: ({ payload }) => {
      remoteApplyRef.current = true;
      const nextData = applyCollabDiffToData(dataRef.current, payload.diff as PuckDiffOp);
      commitDataChange(nextData, {
        broadcast: false,
        ensureDraft: false,
      });
      remoteApplyRef.current = false;
    },
    onConflict: (payload) => {
      setCollabConflict({
        componentId: payload.componentId,
        editedByName: payload.editedByName,
        description: payload.description,
      });
    },
    onCommentEvent: () => {
      void refreshPageComments();
    },
  });

  const commentCounts = useMemo(() => pageComments.reduce<Record<string, number>>((counts, comment) => {
    if (comment.componentId && !comment.resolvedAt) {
      counts[comment.componentId] = (counts[comment.componentId] ?? 0) + 1;
    }
    return counts;
  }, {}), [pageComments]);

  const openCommentPins = useMemo(() => pageComments.reduce<Record<string, Array<{
    id: string;
    x: number;
    y: number;
    tone: "open" | "resolved";
    label: string;
  }>>>((groups, comment) => {
    if (!comment.componentId || comment.resolvedAt || !comment.position) {
      return groups;
    }

    const pins = groups[comment.componentId] ?? [];
    pins.push({
      id: comment.id,
      x: comment.position.x,
      y: comment.position.y,
      tone: "open",
      label: `${comment.authorName}: ${comment.content}`,
    });
    groups[comment.componentId] = pins;
    return groups;
  }, {}), [pageComments]);

  const remoteEditorsByComponent = useMemo(() => {
    const next = new Map<string, Array<{ userId: string; displayName: string; color: string }>>();
    for (const collaborator of collaborators) {
      if (collaborator.userId === currentUser?.userId) {
        continue;
      }
      const componentId = collaborator.cursor?.componentId;
      if (!componentId) {
        continue;
      }

      const currentEditors = next.get(componentId) ?? [];
      currentEditors.push({
        userId: collaborator.userId,
        displayName: collaborator.displayName,
        color: collaborator.color,
      });
      next.set(componentId, currentEditors);
    }
    return next as ReadonlyMap<string, readonly { userId: string; displayName: string; color: string }[]>;
  }, [collaborators, currentUser?.userId]);

  const selectedRemoteEditors = useMemo(
    () => (selectedSectionId ? remoteEditorsByComponent.get(selectedSectionId) ?? [] : []),
    [remoteEditorsByComponent, selectedSectionId],
  );

  const commitDataChange = useCallback((
    nextData: Data,
    options?: {
      broadcast?: boolean;
      ensureDraft?: boolean;
      recordHistory?: boolean;
    },
  ) => {
    const normalized = normalizePageData(nextData);
    const previous = dataRef.current;
    dataRef.current = normalized;
    setData(normalized);

    if (options?.recordHistory !== false) {
      setHistory((prev) => {
        const truncated = [...prev.slice(0, historyIndexRef.current + 1), normalized];
        if (truncated.length > MAX_HISTORY) {
          const trimmed = truncated.slice(truncated.length - MAX_HISTORY);
          historyIndexRef.current = trimmed.length - 1;
          setHistoryIndex(historyIndexRef.current);
          return trimmed;
        }
        historyIndexRef.current = truncated.length - 1;
        setHistoryIndex(historyIndexRef.current);
        return truncated;
      });
    }

    if (options?.ensureDraft !== false) {
      void ensureDraftExists(normalized);
    }

    if (
      options?.broadcast !== false
      && currentPageId
      && !remoteApplyRef.current
    ) {
      const diff = createCollabDiff(previous, normalized);
      if (diff) {
        sendDiff({
          diff,
          description: describeCollabDiff(diff),
          affectedComponentIds: getAffectedComponentIds(diff),
        });
      }
    }
  }, [currentPageId, ensureDraftExists, sendDiff]);

  const handleDataChange = useCallback((newData: Data) => {
    commitDataChange(newData);
  }, [commitDataChange]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      commitDataChange(history[newIndex]!, {
        ensureDraft: false,
        recordHistory: false,
      });
    }
  }, [commitDataChange, historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      commitDataChange(history[newIndex]!, {
        ensureDraft: false,
        recordHistory: false,
      });
    }
  }, [commitDataChange, historyIndex, history]);

  // C14: Keyboard shortcuts for undo/redo
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    if (isSimpleEditor) {
      setShowLayersPanel(false);
      setIsInspectorCollapsed(false);
    }
  }, [isSimpleEditor]);

  useEffect(() => {
    if (!currentPageId || !selectedSectionId) {
      return;
    }

    sendCursor({
      componentId: selectedSectionId,
      relativeY: 0.5,
      scrollY: canvasRef.current?.parentElement?.scrollTop ?? 0,
    });
  }, [currentPageId, selectedSectionId, sendCursor]);


  // C74: SEO panel state
  const [sidebarTab, setSidebarTab] = useState<"style" | "seo" | "analytics" | "copilot" | "dynamic" | "comments" | "versions">(
    requestedEditorMode === "simple" && !isShellEditor ? "copilot" : "style",
  );
  const [dynamicPreviewPreset, setDynamicPreviewPreset] = useState<DynamicPreviewPreset>("default");
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [commentMode, setCommentMode] = useState(false);
  const [pendingCommentTarget, setPendingCommentTarget] = useState<{
    componentId: string;
    componentType: string;
    position: { x: number; y: number };
  } | null>(null);
  const [previewVersion, setPreviewVersion] = useState<PageVersionSummary | null>(null);
  const [previewPublishState, setPreviewPublishState] = useState<"idle" | "publishing" | "error">("idle");
  const [previewPublishMessage, setPreviewPublishMessage] = useState<string | null>(null);
  const [scheduledPublishValue, setScheduledPublishValue] = useState("");
  const [scheduledPublishTimezone, setScheduledPublishTimezone] = useState(readBrowserTimeZone);
  const [isSchedulePanelOpen, setIsSchedulePanelOpen] = useState(false);
  const [scheduleNowMs, setScheduleNowMs] = useState(() => Date.now());
  const [schedulePublishState, setSchedulePublishState] = useState<"idle" | "saving" | "canceling" | "error">("idle");
  const [publishActionMessage, setPublishActionMessage] = useState<string | null>(null);
  const [publishActionTone, setPublishActionTone] = useState<"success" | "error">("success");
  const [seoMeta, setSeoMeta] = useState<SeoMeta>({
    title: "",
    description: "",
    ogImage: "",
    slug: "",
    focusKeyword: "",
  });

  // PB-T.18: Save section to library
  const [showSaveSection, setShowSaveSection] = useState(false);
  const [showSectionLibrary, setShowSectionLibrary] = useState(false);
  const [saveTemplateState, setSaveTemplateState] = useState<"idle" | "saving">("idle");
  const [savedSectionName, setSavedSectionName] = useState("");
  const [savedSectionCategory, setSavedSectionCategory] = useState<SavedSectionCategory>("other");
  const [selectedSectionType, setSelectedSectionType] = useState<string | null>(null);
  const [saveSectionStatus, setSaveSectionStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveSectionMessage, setSaveSectionMessage] = useState<string | null>(null);
  const [sectionContextMenu, setSectionContextMenu] = useState<{
    x: number;
    y: number;
    componentId: string;
    componentType: string | null;
  } | null>(null);

  // C14: Pre-publish checklist
  const [showChecklist, setShowChecklist] = useState(false);
  const [isRunningAccessibilityAudit, setIsRunningAccessibilityAudit] = useState(false);
  const [accessibilityAudit, setAccessibilityAudit] = useState<PublishAccessibilityAuditResult | null>(null);
  const editorComponents = useMemo(
    () => (data.content ?? []).map((item: Record<string, unknown>) => ({
      type: String(item.type ?? ""),
      props: (item.props as Record<string, unknown>) ?? {},
    })),
    [data.content],
  );
  const handleSeoComponentsChange = useCallback((nextComponents: PuckComponent[]) => {
    const currentData = dataRef.current;
    const currentContent = Array.isArray(currentData.content) ? currentData.content : [];
    const nextContent = currentContent.map((item, index) => {
      const nextComponent = nextComponents[index];
      if (!nextComponent || !item || typeof item !== "object") {
        return item;
      }

      const itemRecord = item as Record<string, unknown>;
      const existingProps = itemRecord.props && typeof itemRecord.props === "object"
        ? itemRecord.props as Record<string, unknown>
        : {};

      return {
        ...itemRecord,
        type: nextComponent.type || String(itemRecord.type ?? ""),
        props: {
          ...existingProps,
          ...nextComponent.props,
        },
      };
    });

    commitDataChange({
      ...currentData,
      content: nextContent,
    });
  }, [commitDataChange]);
  const optimizationAnalysis = useMemo(() => analyzeOptimization({
    pageId: currentPageId ?? null,
    components: editorComponents,
    seo: seoMeta,
  }), [currentPageId, editorComponents, seoMeta]);
  const heatmap = optimizationAnalysis.heatmap;
  const heatmapLookup = useMemo(() => buildHeatmapLookup(optimizationAnalysis), [optimizationAnalysis]);
  const consistencyComponents = useMemo(() => readEditorConsistencyComponents(data), [data]);
  const checklistIssues = useMemo(() => {
    const issues: string[] = [];
    const components = (data.content ?? []) as Array<Record<string, unknown>>;
    const images = components.filter((c) => c.type === "Image");
    const missingAlt = images.filter((c) => !(c.props as Record<string, unknown>)?.alt);
    if (missingAlt.length > 0) issues.push(`${missingAlt.length} image(s) missing alt text`);
    if (components.length === 0) issues.push("Page has no content");
    const hasButton = components.some((c) => c.type === "Button" || c.type === "ContactForm");
    if (!hasButton) issues.push("No call-to-action button or form");
    if (accessibilityAudit?.status === "block") {
      issues.push(`Accessibility audit found ${accessibilityAudit.summary.criticalCount} critical issue(s)`);
    } else if (accessibilityAudit?.status === "warn") {
      issues.push(`Accessibility audit found ${accessibilityAudit.summary.violationCount} issue(s) to review`);
    }
    return issues;
  }, [accessibilityAudit, data.content]);

  // Close checklist on Escape
  useEffect(() => {
    if (!showChecklist) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowChecklist(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showChecklist]);

  // C5: Load existing page data from API
  const { page, isLoading: isLoadingPage, error: pageLoadError, refetch: refetchPageDetail } = usePageDetail(isNew ? undefined : pageId);
  const pendingPageSettings = useMemo(() => {
    const nextSettings = { ...(page?.settings ?? {}) } as Record<string, unknown>;
    if (pageMood) {
      nextSettings.mood = pageMood;
    } else {
      delete nextSettings.mood;
    }
    return nextSettings;
  }, [page?.settings, pageMood]);
  const persistSeoMetadata = useCallback(async (targetPageId: string) => {
    const seoPayload = buildSeoMetaPayload(seoMeta);
    const nextSlug = typeof seoPayload.slug === "string" ? seoPayload.slug : "";
    const latestPage = await fetchPageDetail(targetPageId);
    const updatedPage = await updatePageMetadata(targetPageId, latestPage.version, {
      ...(nextSlug ? { slug: nextSlug } : {}),
      seoMeta: seoPayload,
    });

    if (nextSlug && nextSlug !== seoMeta.slug) {
      setSeoMeta((current) => ({ ...current, slug: nextSlug }));
    }

    return updatedPage;
  }, [seoMeta]);
  const effectiveSiteId = requestedSiteId ?? page?.siteId ?? shellSite?.id ?? undefined;
  const filteredPuckConfig = useMemo(
    () => getPuckConfigForPageType((page?.pageType as Parameters<typeof getPuckConfigForPageType>[0]) ?? undefined),
    [page?.pageType],
  );
  const { theme: siteTheme, saveTheme, setTheme: setSiteTheme } = useSiteTheme(effectiveSiteId);
  const editorCanvasThemeStyle = useMemo(
    () => ({
      ...buildEditorCanvasThemeStyle(siteTheme ?? sanitizeSiteTheme({ presetId: SITE_THEME_PRESETS[0]?.id })),
      ...(pageMood ? buildEditorMoodVariables(pageMood) : {}),
      ...tokenPreviewOverrides,
    }),
    [pageMood, siteTheme, tokenPreviewOverrides],
  );
  const tokenPreviewValues = useMemo(
    () => readTokenPreviewValues(editorCanvasThemeStyle),
    [editorCanvasThemeStyle],
  );
  const activeThemePresetId = siteTheme?.presetId ?? SITE_THEME_PRESETS[0]?.id ?? null;

  const handleSaveNow = useCallback(async () => {
    setBuilderError(null);
    setPublishActionMessage(null);

    if (isShellEditor) {
      setPublishActionTone("error");
      setPublishActionMessage("Global shell changes save through republish, not the draft shortcut.");
      return;
    }

    try {
      let targetPageId = currentPageId;

      if (!targetPageId) {
        const createdPage = await createPage({
          title: page?.title?.trim() || "Untitled Page",
          content: dataRef.current,
          siteId: requestedSiteId,
          settings: pendingPageSettings,
          slug: normalizeSeoStateForPersist(seoMeta).slug || undefined,
          seoMeta: buildSeoMetaPayload(seoMeta),
        });
        setCurrentPageId(createdPage.id);
        targetPageId = createdPage.id;
        navigate(`/pages/editor/${createdPage.id}${window.location.search}`, { replace: true });
      } else {
        await savePage(targetPageId, dataRef.current, "manual");
        await persistSeoMetadata(targetPageId);
      }

      setPublishActionTone("success");
      setPublishActionMessage("Draft saved.");
    } catch (error) {
      setPublishActionTone("error");
      setPublishActionMessage(
        error instanceof ApiError ? error.message : "Failed to save the draft.",
      );
    }
  }, [currentPageId, isShellEditor, navigate, page?.title, pendingPageSettings, persistSeoMetadata, requestedSiteId, seoMeta]);

  useEffect(() => {
    setPreviewVersion(null);
  }, [currentPageId, page?.currentVersionId, isShellEditor]);

  useEffect(() => {
    if (!isShellEditor || !requestedSiteId || !shellField) {
      setShellSite(null);
      setIsLoadingShell(false);
      setShellError(null);
      setShellSaveStatus("idle");
      setShellSaveMessage(null);
      return;
    }

    let isCancelled = false;
    setIsLoadingShell(true);
    setShellError(null);
    setBuilderError(null);

    void apiFetch<{ data: SiteShellRecord }>(`/crm/pages/sites/${requestedSiteId}`)
      .then((response) => {
        if (isCancelled) return;
        const site = response.data;
        setShellSite(site);
        const nextData = normalizePageData(readShellData(site[shellField]));
        setData(nextData);
        setHistory([nextData]);
        setHistoryIndex(0);
        setCurrentPageId(undefined);
      })
      .catch((error) => {
        if (isCancelled) return;
        setShellError(error instanceof ApiError ? error.message : "Failed to load the site shell.");
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingShell(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isShellEditor, requestedSiteId, shellField]);

  useEffect(() => {
    if (page?.content) {
      const nextData = normalizePageData(page.content as Data);
      setData(nextData);
      setHistory([nextData]);
      setHistoryIndex(0);
      setCurrentPageId(page.id);
    }
  }, [page]);

  useEffect(() => {
    if (isShellEditor) {
      setPageMood(null);
      return;
    }

    setPageMood(isMoodId(page?.settings?.mood) ? page.settings.mood : null);
  }, [isShellEditor, page?.settings]);

  useEffect(() => {
    if (isShellEditor) {
      setEditorMode("full");
      return;
    }

    setEditorMode(requestedEditorMode ?? "full");
  }, [isShellEditor, requestedEditorMode]);

  useEffect(() => {
    if (isShellEditor) {
      return;
    }

    if (requestedEditorMode === "simple") {
      setSidebarTab("copilot");
      return;
    }

    setSidebarTab((currentTab) => (
      currentTab === "seo" || currentTab === "style" || currentTab === "analytics" || currentTab === "versions"
        ? currentTab
        : "style"
    ));
  }, [isShellEditor, requestedEditorMode]);

  const handleVersionRestore = useCallback(async () => {
    if (!currentPageId) {
      return;
    }

    await refetchPageDetail();
    const latestPage = await fetchPageDetail(currentPageId);
    const nextData = normalizePageData(latestPage.content as Data);
    setPreviewVersion(null);
    dataRef.current = nextData;
    setData(nextData);
    setHistory([nextData]);
    historyIndexRef.current = 0;
    setHistoryIndex(0);
  }, [currentPageId, refetchPageDetail]);

  const handleVersionPreview = useCallback((version: PageVersionSummary) => {
    setPreviewVersion(version);
  }, []);

  const handleExitVersionPreview = useCallback(() => {
    setPreviewVersion(null);
  }, []);

  const handleOpenStagingPreview = useCallback(async () => {
    setPreviewPublishState("publishing");
    setPreviewPublishMessage(null);
    try {
      const audit = await runAccessibilityAudit();
      if (audit.status === "block") {
        setPreviewPublishState("error");
        setShowChecklist(true);
        setPreviewPublishMessage(`Accessibility audit blocked preview publish. Resolve ${audit.summary.criticalCount} critical issue(s) first.`);
        return;
      }

      let targetPageId = currentPageId;
      let targetVersionId: string | undefined = previewVersion?.id ?? undefined;

      if (!targetPageId) {
        const newPage = await createPage({
          title: page?.title?.trim() || "Untitled Page",
          content: data,
          siteId: requestedSiteId,
          settings: pendingPageSettings,
          slug: normalizeSeoStateForPersist(seoMeta).slug || undefined,
          seoMeta: buildSeoMetaPayload(seoMeta),
        });
        setCurrentPageId(newPage.id);
        targetPageId = newPage.id;
        targetVersionId = newPage.currentVersionId ?? undefined;
      } else if (!targetVersionId) {
        await savePage(targetPageId, data, "manual");
        const latestPage = await persistSeoMetadata(targetPageId);
        targetVersionId = latestPage.currentVersionId ?? undefined;
      } else {
        await persistSeoMetadata(targetPageId);
      }

      if (!targetPageId) {
        throw new Error("Preview generation requires a persisted page.");
      }

      const preview = await createPagePreview(targetPageId, targetVersionId, { accessibilityAudit: audit });
      const openedWindow = window.open(preview.previewUrl, "_blank", "noopener,noreferrer");
      setPreviewPublishState("idle");
      const expiryLabel = new Date(preview.expiresAt).toLocaleString();
      setPreviewPublishMessage(
        openedWindow
          ? `Staging preview opened in a new tab. Expires ${expiryLabel}. ${preview.scheduledPublishGap ?? ""}`.trim()
          : `Staging preview is ready at ${preview.previewUrl}. Expires ${expiryLabel}. ${preview.scheduledPublishGap ?? ""}`.trim(),
      );
    } catch (error) {
      setPreviewPublishState("error");
      setPreviewPublishMessage(
        error instanceof ApiError ? error.message : "Failed to generate a staging preview.",
      );
    }
  }, [currentPageId, data, page?.title, pendingPageSettings, persistSeoMetadata, previewVersion, requestedSiteId, runAccessibilityAudit, seoMeta]);

  const handleApplyThemePreset = useCallback(async (presetId: string) => {
    if (!effectiveSiteId) {
      return;
    }

    const nextTheme = sanitizeSiteTheme({ presetId });
    const previousTheme = siteTheme;
    setThemeSaveState("saving");
    setThemeSaveMessage(null);
    setSiteTheme(nextTheme);

    try {
      await saveTheme(nextTheme);
      setThemeSaveState("saved");
      setThemeSaveMessage(`Applied the ${SITE_THEME_PRESETS.find((preset) => preset.id === presetId)?.name ?? presetId} preset to this site.`);
      setShowThemePicker(false);
      setShellSite((current) => current ? { ...current, globalTheme: nextTheme } : current);
    } catch (error) {
      setSiteTheme(previousTheme);
      setThemeSaveState("error");
      setThemeSaveMessage(
        error instanceof ApiError ? error.message : "Failed to save the site theme preset.",
      );
    }
  }, [effectiveSiteId, saveTheme, setSiteTheme, siteTheme]);

  const handleLiveTokenPreviewChange = useCallback((token: string, value: string) => {
    setTokenPreviewOverrides((current) => ({
      ...current,
      [token]: value,
    }));
  }, []);

  const handleLiveTokenPreviewResetAll = useCallback(() => {
    setTokenPreviewOverrides({});
  }, []);

  const handleApplyMood = useCallback(async (nextMood: MoodId | null) => {
    const previousMood = pageMood;
    setPageMood(nextMood);
    setShowMoodPicker(false);
    setPublishActionTone("success");

    if (!currentPageId || isShellEditor) {
      setPublishActionMessage(
        nextMood
          ? "Mood preview applied. It will persist when this page is first created."
          : "Mood preview reset to the site theme. It will persist when this page is first created.",
      );
      return;
    }

    try {
      const latestPage = await fetchPageDetail(currentPageId);
      const nextSettings = { ...latestPage.settings } as Record<string, unknown>;
      if (nextMood) {
        nextSettings.mood = nextMood;
      } else {
        delete nextSettings.mood;
      }
      await updatePageSettings(currentPageId, latestPage.version, nextSettings);
      await refetchPageDetail();
      setPublishActionMessage(
        nextMood
          ? "Page mood saved."
          : "Page mood cleared. The page now uses the site theme only.",
      );
    } catch (error) {
      setPageMood(previousMood);
      setPublishActionTone("error");
      setPublishActionMessage(
        error instanceof ApiError ? error.message : "Failed to save the page mood.",
      );
    }
  }, [currentPageId, isShellEditor, pageMood, refetchPageDetail]);

  const editorData = useMemo(
    () => (previewVersion
      ? normalizePageData(previewVersion.puckData as Data)
      : data),
    [data, previewVersion],
  );

  async function runAccessibilityAudit(): Promise<PublishAccessibilityAuditResult> {
    type AxeWindow = Window & typeof globalThis & {
      axe: Pick<typeof axe, "run">;
    };

    setIsRunningAccessibilityAudit(true);
    try {
      const renderResult = renderPublishedHtml({
        puckData: editorData,
        pageTitle: page?.title?.trim() || "Untitled Page",
        seoMeta,
        settings: pendingPageSettings,
        submissionEndpoint: `${window.location.origin}/_ease/submissions`,
        pageId: currentPageId ?? "draft-page",
        tenantId: "editor-preview",
        versionId: previewVersion?.id ?? page?.currentVersionId ?? "draft-version",
      });

      const iframe = window.document.createElement("iframe");
      iframe.setAttribute("sandbox", "allow-same-origin allow-scripts");
      iframe.style.position = "absolute";
      iframe.style.width = "1440px";
      iframe.style.height = "900px";
      iframe.style.left = "-10000px";
      iframe.style.top = "0";
      iframe.style.opacity = "0";
      window.document.body.appendChild(iframe);

      try {
        const doc = iframe.contentDocument;
        if (!doc) {
          throw new Error("Accessibility audit could not create a preview document.");
        }

        doc.open();
        doc.write(renderResult.html);
        doc.close();

        const frameWindow = iframe.contentWindow as AxeWindow | null;
        if (!frameWindow) {
          throw new Error("Accessibility audit could not access the preview window.");
        }
        frameWindow.eval(axe.source);

        const results = await frameWindow.axe.run(doc, {
          runOnly: {
            type: "tag",
            values: ["wcag2a", "wcag2aa"],
          },
        });
        const normalized = normalizeAccessibilityAudit(results);
        setAccessibilityAudit(normalized);
        return normalized;
      } finally {
        iframe.remove();
      }
    } finally {
      setIsRunningAccessibilityAudit(false);
    }
  }

  const handleToggleChecklist = useCallback(() => {
    setShowChecklist((current) => {
      const next = !current;
      if (next) {
        void runAccessibilityAudit().catch((error) => {
          setBuilderError(
            error instanceof Error ? error.message : "Accessibility audit failed.",
          );
        });
      }
      return next;
    });
  }, [runAccessibilityAudit]);

  const handleCopilotApplyResult = useCallback((result: OptimizationApplyResult) => {
    const nextData = applyOptimizationResultToEditorData(data, result);

    if (JSON.stringify(nextData) !== JSON.stringify(data)) {
      handleDataChange(nextData);
    }

    setSeoMeta((current) => ({
      ...current,
      ...result.seo,
    }));
  }, [data]);

  const handleConsistencyFix = useCallback((
    fixes: ReadonlyArray<{ componentId: string; prop: string; value: unknown }>,
  ) => {
    const nextData = applyConsistencyFixesToEditorData(dataRef.current, fixes);
    if (JSON.stringify(nextData) !== JSON.stringify(dataRef.current)) {
      handleDataChange(nextData);
    }
  }, [handleDataChange]);

  const handleRunThreeSecondTest = useCallback(async () => {
    setShowThreeSecondTestPanel(true);
    setThreeSecondTestError(null);

    const input = extractThreeSecondTestInput(editorData as {
      content?: Array<{ type?: string | null; props?: Record<string, unknown> | null }> | null;
    });
    setThreeSecondTestInput(input);

    if (!input) {
      setThreeSecondTestResult(null);
      setThreeSecondTestError("Add a hero block with a real headline before running the 3-second test.");
      return;
    }

    setIsRunningThreeSecondTest(true);

    try {
      const response = await apiFetch<{ data: ThreeSecondTestResult }>("/crm/pages/generate/three-second-test", {
        method: "POST",
        body: JSON.stringify(input),
      });
      setThreeSecondTestResult(response.data);
    } catch (error) {
      setThreeSecondTestResult(null);
      setThreeSecondTestError(formatThreeSecondTestError(error));
    } finally {
      setIsRunningThreeSecondTest(false);
    }
  }, [editorData]);

  useEffect(() => {
    if (sidebarTab !== "copilot" && showHeatmap) {
      setShowHeatmap(false);
    }
  }, [showHeatmap, sidebarTab]);

  // C74: Sync SEO meta from loaded page
  useEffect(() => {
    if (isShellEditor && shellSite && shellType) {
      setSavedSectionName(`${shellSite.name} ${shellType}`);
      return;
    }

    if (page) {
      const loadedSeo = page.seoMeta ?? {};
      setSeoMeta({
        ...loadedSeo,
        title: typeof loadedSeo.title === "string" && loadedSeo.title.length > 0
          ? loadedSeo.title
          : page.title || "",
        description: typeof loadedSeo.description === "string" ? loadedSeo.description : "",
        ogImage: typeof loadedSeo.ogImage === "string" ? loadedSeo.ogImage : "",
        slug: typeof loadedSeo.slug === "string" && loadedSeo.slug.length > 0
          ? loadedSeo.slug
          : page.slug || "",
        focusKeyword: typeof loadedSeo.focusKeyword === "string" ? loadedSeo.focusKeyword : "",
      });
      setSavedSectionName(page.title ? `${page.title} section` : "Reusable section");
    }
  }, [page]);

  useEffect(() => {
    if (isShellEditor) {
      setScheduledPublishValue("");
      setIsSchedulePanelOpen(false);
      return;
    }

    if (page?.scheduledPublishAt) {
      setScheduledPublishValue(formatDateTimeLocalForTimeZone(new Date(page.scheduledPublishAt), scheduledPublishTimezone));
      setIsSchedulePanelOpen(true);
      return;
    }

    setScheduledPublishValue("");
  }, [isShellEditor, page?.scheduledPublishAt, scheduledPublishTimezone]);

  useEffect(() => {
    if (!page?.scheduledPublishAt) {
      return undefined;
    }

    const interval = window.setInterval(() => setScheduleNowMs(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, [page?.scheduledPublishAt]);

  // C5: Auto-save (debounced 2s)
  const saveStatus = useAutoSave(isShellEditor ? undefined : currentPageId, data);

  // C5: Publish handler
  const handlePublish = async () => {
    setBuilderError(null);
    setPublishActionMessage(null);

    if (isShellEditor && requestedSiteId && shellField) {
      setShellSaveStatus("saving");
      setShellSaveMessage(null);

      try {
        await apiFetch(`/crm/pages/sites/${requestedSiteId}`, {
          method: "PUT",
          body: JSON.stringify({ [shellField]: data }),
        });

        const publishResponse = await apiFetch<{ data: { jobId: string; totalPages: number } }>(
          `/crm/pages/sites/${requestedSiteId}/publish`,
          { method: "POST", body: JSON.stringify({}) },
        );

        setShellSite((current) => current
          ? { ...current, [shellField]: data }
          : current);
        setShellSaveStatus("saved");
        setShellSaveMessage(
          `Saved global ${shellType} and queued a site republish for ${publishResponse.data.totalPages} page${publishResponse.data.totalPages === 1 ? "" : "s"}.`,
        );
      } catch (error) {
        setShellSaveStatus("error");
        setShellSaveMessage(
          error instanceof ApiError ? error.message : `Failed to save the global ${shellType}.`,
        );
      }
      return;
    }

    try {
      const audit = await runAccessibilityAudit();
      if (audit.status === "block") {
        setShowChecklist(true);
        setBuilderError(`Accessibility audit blocked publish. Resolve ${audit.summary.criticalCount} critical issue(s) first.`);
        return;
      }

      let publishedPageId: string | null = null;

      if (isNew && !currentPageId) {
        // Create the page first
        const newPage = await createPage({
          title: "Untitled Page",
          content: data,
          siteId: requestedSiteId,
          settings: pendingPageSettings,
          slug: normalizeSeoStateForPersist(seoMeta).slug || undefined,
          seoMeta: buildSeoMetaPayload(seoMeta),
        });
        setCurrentPageId(newPage.id);
        await publishPage(newPage.id, { accessibilityAudit: audit });
        publishedPageId = newPage.id;
      } else if (currentPageId) {
        await persistSeoMetadata(currentPageId);
        await publishPage(currentPageId, { accessibilityAudit: audit });
        publishedPageId = currentPageId;
      } else {
        throw new Error("This page is not persisted yet, so publish cannot continue.");
      }

      const didFireConfetti = maybeFireFirstPublishConfetti(publishedPageId);
      if (didFireConfetti) {
        await new Promise((resolve) => {
          setTimeout(resolve, 700);
        });
      }

      navigate("/pages/sites");
    } catch (error) {
      setBuilderError(
        error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Failed to publish this page.",
      );
    }
  };

  const handleSchedulePublish = async () => {
    setBuilderError(null);
    setPublishActionMessage(null);

    if (isShellEditor) {
      setBuilderError("Global shells publish through the site pipeline, not per-page scheduling.");
      return;
    }

    if (!scheduledPublishValue) {
      setBuilderError("Choose a date and time before scheduling the publish.");
      return;
    }

    const scheduledAt = dateTimeLocalToUtc(scheduledPublishValue, scheduledPublishTimezone);
    if (!scheduledAt || Number.isNaN(scheduledAt.getTime())) {
      setBuilderError("Choose a valid date, time, and timezone for the scheduled publish.");
      return;
    }

    if (scheduledAt.getTime() - Date.now() < SCHEDULE_MIN_LEAD_MS) {
      setBuilderError("Schedule publish at least 1 hour from now so final checks can run.");
      return;
    }

    setSchedulePublishState("saving");

    try {
      let targetPageId = currentPageId;
      let expectedVersion = page?.version ?? 1;
      let versionId = previewVersion?.id ?? undefined;

      if (!targetPageId) {
        const createdPage = await createPage({
          title: page?.title?.trim() || "Untitled Page",
          content: data,
          siteId: requestedSiteId,
          settings: pendingPageSettings,
          slug: normalizeSeoStateForPersist(seoMeta).slug || undefined,
          seoMeta: buildSeoMetaPayload(seoMeta),
        });
        setCurrentPageId(createdPage.id);
        targetPageId = createdPage.id;
        expectedVersion = createdPage.version;
        versionId = createdPage.currentVersionId ?? undefined;
      }

      if (!targetPageId) {
        throw new Error("Scheduling requires a persisted page record.");
      }

      if (!versionId) {
        await savePage(targetPageId, data, "manual");
        const latestPage = await persistSeoMetadata(targetPageId);
        expectedVersion = latestPage.version;
        versionId = latestPage.currentVersionId ?? undefined;
      } else {
        const latestPage = await persistSeoMetadata(targetPageId);
        expectedVersion = latestPage.version;
      }

      if (!versionId) {
        throw new Error("Save the page first so a concrete version can be scheduled.");
      }

      const result = await schedulePagePublish(targetPageId, {
        expectedVersion,
        publishAt: scheduledAt.toISOString(),
        versionId,
      });
      await refetchPageDetail();
      if (result.scheduledPublishAt) {
        setScheduledPublishValue(formatDateTimeLocalForTimeZone(new Date(result.scheduledPublishAt), scheduledPublishTimezone));
      }
      setPublishActionTone("success");
      setPublishActionMessage(
        result.scheduledPublishAt
          ? `Scheduled to go live on ${formatScheduleTime(result.scheduledPublishAt, scheduledPublishTimezone)}.`
          : "Publish schedule saved.",
      );
      setSchedulePublishState("idle");
    } catch (error) {
      setSchedulePublishState("error");
      setBuilderError(
        error instanceof ApiError ? error.message : "Failed to schedule this page for publishing.",
      );
    }
  };

  const handleCancelScheduledPublish = async () => {
    setBuilderError(null);
    setPublishActionMessage(null);

    if (!currentPageId || !page) {
      setBuilderError("The page needs a real mounted record before its schedule can be cancelled.");
      return;
    }

    setSchedulePublishState("canceling");

    try {
      const latestPage = await fetchPageDetail(currentPageId);
      await cancelScheduledPagePublish(currentPageId, latestPage.version);
      await refetchPageDetail();
      setScheduledPublishValue("");
      setPublishActionTone("success");
      setPublishActionMessage("Scheduled publish cancelled. The page remains a draft until you publish it.");
      setSchedulePublishState("idle");
    } catch (error) {
      setSchedulePublishState("error");
      setBuilderError(
        error instanceof ApiError ? error.message : "Failed to cancel the scheduled publish.",
      );
    }
  };

  const handleSaveSection = async () => {
    setSaveSectionStatus("saving");
    setSaveSectionMessage(null);

    try {
      const puckData = buildSavedSectionPayloadForSelection(data, selectedSectionId);

      await createSavedSection({
        name: savedSectionName || `${selectedSectionType ?? page?.title ?? "Untitled"} section`,
        category: savedSectionCategory,
        thumbnail: buildGeneratedSectionThumbnail(savedSectionCategory),
        puckData,
      });

      setSaveSectionStatus("saved");
      setSaveSectionMessage("Saved to your section library.");
      setShowSaveSection(false);
    } catch (error) {
      setSaveSectionStatus("error");
      setSaveSectionMessage(error instanceof Error ? error.message : "Failed to save section");
    }
  };

  const handleOpenSaveSectionDialog = useCallback((componentType = selectedSectionType) => {
    if (!selectedSectionId) {
      return;
    }

    const category = inferSavedSectionCategory(componentType);
    setSavedSectionCategory(category);
    setSavedSectionName(`${componentType ?? page?.title ?? "Reusable"} section`);
    setSaveSectionStatus("idle");
    setSaveSectionMessage(null);
    setSectionContextMenu(null);
    setShowSaveSection(true);
  }, [page?.title, selectedSectionId, selectedSectionType]);

  const handleCanvasContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (isSimpleEditor || previewVersion) {
      return;
    }

    const target = event.target instanceof HTMLElement
      ? event.target.closest("[data-puck-component]")
      : null;
    if (!target || !event.currentTarget.contains(target)) {
      return;
    }

    const componentId = target.getAttribute("data-puck-component");
    if (!componentId) {
      return;
    }

    event.preventDefault();
    const componentType = findComponentTypeById(dataRef.current, componentId);
    setSelectedSectionId(componentId);
    setSelectedSectionType(componentType);
    setSavedSectionName(`${componentType ?? "Reusable"} section`);
    setSavedSectionCategory(inferSavedSectionCategory(componentType));
    setSaveSectionStatus("idle");
    setSaveSectionMessage(null);
    setShowSaveSection(false);
    setSectionContextMenu({
      x: event.clientX,
      y: event.clientY,
      componentId,
      componentType,
    });
  }, [isSimpleEditor, previewVersion]);

  useEffect(() => {
    if (!sectionContextMenu) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSectionContextMenu(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [sectionContextMenu]);

  const handleInsertSection = async (template: SectionInsertTemplate) => {
    const insertable = cloneSavedSectionForInsertion(
      template.puckData ?? { content: template.content },
    );

    handleDataChange({
      ...data,
      content: [...(Array.isArray(data.content) ? data.content : []), ...insertable.content],
      zones: mergeZones(data.zones, insertable.zones),
    });
    setShowSectionLibrary(false);

    if (!template.savedSectionId) {
      return;
    }

    try {
      await recordSavedSectionUse(template.savedSectionId);
    } catch (error) {
      setPublishActionTone("error");
      setPublishActionMessage(
        error instanceof Error
          ? `Section inserted, but usage tracking failed: ${error.message}`
          : "Section inserted, but usage tracking failed.",
      );
    }
  };

  const handleSaveAsTemplate = useCallback(async () => {
    if (isShellEditor) {
      setPublishActionTone("error");
      setPublishActionMessage("Global shell content cannot be saved as a reusable page template.");
      return;
    }

    setSaveTemplateState("saving");
    setPublishActionMessage(null);

    try {
      let targetPageId = currentPageId;
      let baseTitle = page?.title?.trim() || "Untitled Page";

      if (!targetPageId) {
        const createdPage = await createPage({
          title: baseTitle,
          content: dataRef.current,
          siteId: requestedSiteId,
          settings: pendingPageSettings,
          slug: normalizeSeoStateForPersist(seoMeta).slug || undefined,
          seoMeta: buildSeoMetaPayload(seoMeta),
        });
        setCurrentPageId(createdPage.id);
        targetPageId = createdPage.id;
        baseTitle = createdPage.title.trim() || baseTitle;
        navigate(`/pages/editor/${createdPage.id}${window.location.search}`, { replace: true });
      } else {
        await savePage(targetPageId, dataRef.current, "manual");
        await persistSeoMetadata(targetPageId);
      }

      const normalizedBaseTitle = baseTitle.replace(/\s+Template$/i, "").trim() || "Untitled Page";
      const savedTemplate = await duplicatePage({
        pageId: targetPageId,
        title: `${normalizedBaseTitle} Template`,
        mode: "personal_template",
      });

      setPublishActionTone("success");
      setPublishActionMessage(
        `${savedTemplate.title} is now a private template. Reapply it from the new-page flow or the sites workspace.`,
      );
    } catch (error) {
      setPublishActionTone("error");
      setPublishActionMessage(
        error instanceof ApiError ? error.message : "Failed to save the current page as a private template.",
      );
    } finally {
      setSaveTemplateState("idle");
    }
  }, [currentPageId, dataRef, isShellEditor, navigate, page?.title, pendingPageSettings, persistSeoMetadata, requestedSiteId, seoMeta]);

  const handleDeleteSelectedComponent = useCallback(() => {
    if (!selectedSectionId) {
      return;
    }

    const nextData = removeSelectedSubtree(dataRef.current, selectedSectionId);
    if (nextData === dataRef.current) {
      return;
    }

    commitDataChange(nextData);
    setShowSaveSection(false);
    setSelectedSectionId(null);
  }, [commitDataChange, selectedSectionId]);

  const handleDuplicateSelectedComponent = useCallback(() => {
    if (!selectedSectionId) {
      return;
    }

    const nextData = duplicateSelectedSubtree(dataRef.current, selectedSectionId);
    commitDataChange(nextData);
  }, [commitDataChange, selectedSectionId]);

  const handleMoveSelectedComponent = useCallback((direction: -1 | 1) => {
    if (!selectedSectionId) {
      return;
    }

    const nextData = moveSelectedBlock(dataRef.current, selectedSectionId, direction);
    commitDataChange(nextData);
  }, [commitDataChange, selectedSectionId]);

  const handleCopySelectedComponent = useCallback(() => {
    if (!selectedSectionId) {
      return;
    }

    setComponentClipboard(buildSavedSectionPayloadForSelection(dataRef.current, selectedSectionId));
  }, [selectedSectionId]);

  const handleCutSelectedComponent = useCallback(() => {
    if (!selectedSectionId) {
      return;
    }

    setComponentClipboard(buildSavedSectionPayloadForSelection(dataRef.current, selectedSectionId));
    const nextData = removeSelectedSubtree(dataRef.current, selectedSectionId);
    commitDataChange(nextData);
    setShowSaveSection(false);
    setSelectedSectionId(null);
  }, [commitDataChange, selectedSectionId]);

  const handlePasteComponent = useCallback(() => {
    if (!componentClipboard) {
      return;
    }

    const nextData = insertSavedSectionNearSelection(
      dataRef.current,
      selectedSectionId,
      componentClipboard,
    );
    commitDataChange(nextData);
  }, [commitDataChange, componentClipboard, selectedSectionId]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const hasPrimaryModifier = event.metaKey || event.ctrlKey;
      const isEditableTarget = isEditableKeyboardTarget(event.target);
      const canMutateCanvas = !previewVersion;

      if (key === "escape") {
        const shouldCloseSomething = showShortcutsOverlay
          || showChecklist
          || showSectionLibrary
          || showSaveSection
          || commentMode;
        if (shouldCloseSomething) {
          event.preventDefault();
          setShowShortcutsOverlay(false);
          setShowChecklist(false);
          setShowSectionLibrary(false);
          setShowSaveSection(false);
          setCommentMode(false);
          setPendingCommentTarget(null);
        }
        return;
      }

      if (isEditableTarget) {
        return;
      }

      if (hasPrimaryModifier && key === "/") {
        event.preventDefault();
        setShowShortcutsOverlay((current) => !current);
        return;
      }

      if (event.altKey && !hasPrimaryModifier && key === "1") {
        event.preventDefault();
        setPreviewDevice("desktop");
        return;
      }

      if (event.altKey && !hasPrimaryModifier && key === "2") {
        event.preventDefault();
        setPreviewDevice("tablet");
        return;
      }

      if (event.altKey && !hasPrimaryModifier && key === "3") {
        event.preventDefault();
        setPreviewDevice("mobile");
        return;
      }

      if (hasPrimaryModifier && event.shiftKey && key === "c") {
        event.preventDefault();
        setSidebarTab("comments");
        return;
      }

      if (hasPrimaryModifier && event.shiftKey && key === "s") {
        event.preventDefault();
        setSidebarTab("style");
        return;
      }

      if (hasPrimaryModifier && key === ".") {
        event.preventDefault();
        if (!isSimpleEditor) {
          setShowLayersPanel((current) => !current);
        }
        return;
      }

      if (hasPrimaryModifier && key === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }

      if (hasPrimaryModifier && key === "s" && !event.shiftKey) {
        event.preventDefault();
        void handleSaveNow();
        return;
      }

      if (hasPrimaryModifier && key === "p" && !event.shiftKey) {
        event.preventDefault();
        void handleOpenStagingPreview();
        return;
      }

      if (hasPrimaryModifier && event.shiftKey && key === "p") {
        event.preventDefault();
        if (!previewVersion) {
          void handlePublish();
        }
        return;
      }

      if (!canMutateCanvas) {
        return;
      }

      if (hasPrimaryModifier && key === "c") {
        event.preventDefault();
        handleCopySelectedComponent();
        return;
      }

      if (hasPrimaryModifier && key === "x") {
        event.preventDefault();
        handleCutSelectedComponent();
        return;
      }

      if (hasPrimaryModifier && key === "v") {
        event.preventDefault();
        handlePasteComponent();
        return;
      }

      if (hasPrimaryModifier && key === "d") {
        event.preventDefault();
        handleDuplicateSelectedComponent();
        return;
      }

      if (event.altKey && !hasPrimaryModifier && event.key === "ArrowUp") {
        event.preventDefault();
        handleMoveSelectedComponent(-1);
        return;
      }

      if (event.altKey && !hasPrimaryModifier && event.key === "ArrowDown") {
        event.preventDefault();
        handleMoveSelectedComponent(1);
        return;
      }

      if (hasPrimaryModifier && key === "[") {
        event.preventDefault();
        handleMoveSelectedComponent(-1);
        return;
      }

      if (hasPrimaryModifier && key === "]") {
        event.preventDefault();
        handleMoveSelectedComponent(1);
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        handleDeleteSelectedComponent();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    commentMode,
    handleCopySelectedComponent,
    handleCutSelectedComponent,
    handleDeleteSelectedComponent,
    handleDuplicateSelectedComponent,
    handleMoveSelectedComponent,
    handleOpenStagingPreview,
    handlePasteComponent,
    handlePublish,
    handleRedo,
    handleSaveNow,
    handleUndo,
    isSimpleEditor,
    previewVersion,
    showChecklist,
    showSaveSection,
    showSectionLibrary,
    showShortcutsOverlay,
  ]);

  const handleCommentTarget = useCallback((
    componentId: string,
    componentType: string,
    position: { x: number; y: number },
  ) => {
    setSidebarTab("comments");
    setCommentMode(true);
    setPendingCommentTarget({
      componentId,
      componentType,
      position,
    });
  }, []);

  const handleCreatePageComment = useCallback(async (input: {
    anchorType: "page" | "component";
    componentId?: string | null;
    componentType?: string | null;
    position?: { x: number; y: number } | null;
    content: string;
    pageVersionId?: string | null;
  }) => {
    const created = await createComment({
      ...input,
      position:
        input.position
        ?? (
          input.anchorType === "component"
          && pendingCommentTarget
          && pendingCommentTarget.componentId === (input.componentId ?? null)
            ? pendingCommentTarget.position
            : null
        ),
    });
    sendCommentEvent({
      eventType: "added",
      commentId: created.id,
      componentId: created.componentId,
      componentType: created.componentType,
      authorName: created.authorName,
    });
    setPendingCommentTarget(null);
    return created;
  }, [createComment, pendingCommentTarget, sendCommentEvent]);

  const handleResolvePageComment = useCallback(async (commentId: string) => {
    const resolved = await markResolved(commentId);
    sendCommentEvent({
      eventType: "resolved",
      commentId: resolved.id,
      componentId: resolved.componentId,
      componentType: resolved.componentType,
      resolvedBy: resolved.resolvedBy,
    });
    return resolved;
  }, [markResolved, sendCommentEvent]);

  const handleReplyToPageComment = useCallback(async (
    commentId: string,
    input: { content: string },
  ) => {
    const reply = await replyToComment(commentId, input);
    sendCommentEvent({
      eventType: "added",
      commentId: reply.id,
      componentId: reply.componentId,
      componentType: reply.componentType,
      authorName: reply.authorName,
    });
    return reply;
  }, [replyToComment, sendCommentEvent]);

  const handleFocusPageComment = useCallback((input: {
    anchorType: "page" | "component";
    componentId: string | null;
    componentType: string | null;
    commentId: string;
  }) => {
    setSidebarTab("comments");
    setCommentMode(false);
    setPendingCommentTarget(null);

    const highlightTarget = (element: HTMLElement | null) => {
      if (!element) {
        return;
      }

      element.animate(
        [
          { boxShadow: "0 0 0 0 rgba(245, 158, 11, 0.45)" },
          { boxShadow: "0 0 0 10px rgba(245, 158, 11, 0.18)" },
          { boxShadow: "0 0 0 0 rgba(245, 158, 11, 0)" },
        ],
        {
          duration: 1200,
          easing: "ease-out",
        },
      );
    };

    if (input.anchorType === "component" && input.componentId) {
      setSelectedSectionId(input.componentId);
      setSelectedSectionType(input.componentType ?? findComponentTypeById(dataRef.current, input.componentId));

      const selectorValue = globalThis.CSS?.escape
        ? globalThis.CSS.escape(input.componentId)
        : input.componentId.replaceAll("\"", "\\\"");
      const element = canvasRef.current?.querySelector(`[data-block-id="${selectorValue}"]`) as HTMLElement | null;
      element?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      highlightTarget(element);
      return;
    }

    setSelectedSectionId(null);
    setSelectedSectionType(null);
    canvasRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    highlightTarget(canvasRef.current);
  }, []);

  // Save status indicator
  const saveIndicator = useMemo(() => {
    if (isShellEditor) {
      if (shellSaveStatus === "saving") return <span className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Saving shell...</span>;
      if (shellSaveStatus === "saved") return <span className="flex items-center gap-1 text-xs text-[var(--color-success)]"><Check className="h-3 w-3" />Shell saved</span>;
      if (shellSaveStatus === "error") return <span className="flex items-center gap-1 text-xs text-[var(--color-destructive)]"><AlertCircle className="h-3 w-3" />Shell save failed</span>;
      return null;
    }

    if (saveStatus === "saving") return <span className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Saving...</span>;
    if (saveStatus === "saved") return <span className="flex items-center gap-1 text-xs text-[var(--color-success)]"><Check className="h-3 w-3" />Saved</span>;
    if (saveStatus === "error") return <span className="flex items-center gap-1 text-xs text-[var(--color-destructive)]"><AlertCircle className="h-3 w-3" />Save failed</span>;
    return null;
  }, [isShellEditor, saveStatus, shellSaveStatus]);

  const continuationTitle = useMemo(() => {
    if (editorEntry === "template") {
      return "Turn this starter into your page.";
    }

    if (editorEntry === "generated") {
      return "Polish the approved draft.";
    }

    return "Keep editing without the heavy controls first.";
  }, [editorEntry]);

  const continuationDescription = useMemo(() => {
    if (editorEntry === "template") {
      return "Replace the starter copy, photos, and offers first. Once the page feels like yours, open full controls for the deeper settings.";
    }

    if (editorEntry === "generated") {
      return "Your reviewed draft is already here. Tighten the words, swap visuals, and check the main call-to-action before you move to deeper builder controls.";
    }

    return "Make the next few changes here, then open full controls only if you want the deeper settings.";
  }, [editorEntry]);

  const scheduleTimeZoneOptions = useMemo(
    () => buildScheduleTimeZoneOptions(scheduledPublishTimezone),
    [scheduledPublishTimezone],
  );
  const scheduledPublishMinimumValue = useMemo(
    () => formatDateTimeLocalForTimeZone(new Date(scheduleNowMs + SCHEDULE_MIN_LEAD_MS), scheduledPublishTimezone),
    [scheduleNowMs, scheduledPublishTimezone],
  );
  const scheduledPublishLabel = useMemo(
    () => formatScheduleTime(page?.scheduledPublishAt, scheduledPublishTimezone),
    [page?.scheduledPublishAt, scheduledPublishTimezone],
  );
  const scheduledPublishCountdown = useMemo(
    () => formatScheduleCountdown(page?.scheduledPublishAt, scheduleNowMs),
    [page?.scheduledPublishAt, scheduleNowMs],
  );

  if (isLoadingPage || isLoadingShell) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (pageLoadError && !page && !isNew && !isShellEditor) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-2xl rounded-[28px] border border-destructive/25 bg-destructive/5 p-8 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="space-y-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Page editor unavailable</h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                  The mounted page contract failed to load, so the editor cannot safely fall back to a blank draft.
                </p>
              </div>
              <div className="rounded-2xl border border-destructive/20 bg-background/80 px-4 py-3 text-sm text-destructive">
                {pageLoadError}
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate(0)}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                >
                  Retry load
                </button>
                <button
                  type="button"
                  onClick={() => navigate(backHref)}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                >
                  Back to pages
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background">
      {/* Editor toolbar */}
      <div className="flex h-12 items-center justify-between border-b border-border bg-card px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backHref)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Back to pages"
          >
            ← Back
          </button>
          <span className="text-sm font-medium text-foreground">
            {isShellEditor && shellSite && shellType
              ? `Global ${shellType} · ${shellSite.name}`
              : isSimpleEditor
                ? page?.title ? `Continue: ${page.title}` : "Continue editing"
              : isNew
                ? "New Page"
                : page?.title ?? `Editing: ${pageId}`}
          </span>
          {!isShellEditor && currentPageId ? (
            <CollaborationBar collaborators={collaborators} isConnected={isConnected} connectError={connectError} />
          ) : null}
          {saveIndicator}
        </div>
        <div className="flex items-center gap-2">
          {/* C14: Undo/Redo */}
          <div className="flex items-center gap-0.5 border-r border-border pr-2 mr-1">
            <button onClick={handleUndo} disabled={historyIndex <= 0} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-30" title="Undo (Cmd+Z)">
              <Undo2 className="h-4 w-4" />
            </button>
            <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-30" title="Redo (Cmd+Shift+Z)">
              <Redo2 className="h-4 w-4" />
            </button>
          </div>
          {!isShellEditor ? (
            <button
              type="button"
              onClick={() => void handleOpenStagingPreview()}
              disabled={previewPublishState === "publishing"}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {previewPublishState === "publishing" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ExternalLink className="h-3.5 w-3.5" />
              )}
              Preview
            </button>
          ) : null}
          {!isSimpleEditor && effectiveSiteId ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowThemePicker((current) => !current)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition hover:bg-accent"
                title="Apply a site-wide theme preset to the live editor canvas and persisted site theme"
              >
                <EditorThemeButtonIcon className="h-3.5 w-3.5" />
                {EditorThemeButtonLabel(activeThemePresetId)}
              </button>
              {showThemePicker ? (
                <>
                  <EditorThemePresetPicker
                    activePresetId={activeThemePresetId}
                    isSaving={themeSaveState === "saving"}
                    isDisabled={!effectiveSiteId}
                    onSelectPreset={(preset) => void handleApplyThemePreset(preset.id)}
                    onOpenSettings={() => navigate(`/pages/theme?siteId=${effectiveSiteId}`)}
                  />
                  <div className="absolute right-[372px] top-full z-20 mt-2 h-[480px] w-[360px] overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                    <LiveTokenPreview
                      tokenValues={tokenPreviewValues}
                      onTokenChange={handleLiveTokenPreviewChange}
                      onResetAll={handleLiveTokenPreviewResetAll}
                    />
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
          {!isShellEditor && !isSimpleEditor ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMoodPicker((current) => !current)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition hover:bg-accent"
                title="Apply a page-level mood override to this editor canvas and published page"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {pageMood ? `Mood: ${pageMood}` : "Mood"}
              </button>
              {showMoodPicker ? (
                <div className="absolute right-0 top-full z-20 mt-2 w-[360px] rounded-2xl border border-border bg-card p-3 shadow-xl">
                  <MoodPicker currentMoodId={pageMood} onApply={(moodId) => void handleApplyMood(moodId)} />
                </div>
              ) : null}
            </div>
          ) : null}
          {/* Sidebar toggle */}
          {showSidebar ? (
            <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5">
              <button
                onClick={() => setSidebarTab("style")}
                className={`rounded-md px-2 py-1 text-xs transition-colors ${sidebarTab === "style" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                Style
              </button>
              {!isShellEditor ? (
                <button
                  onClick={() => setSidebarTab("analytics")}
                  className={`rounded-md px-2 py-1 text-xs transition-colors flex items-center gap-1 ${sidebarTab === "analytics" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
                >
                  <BarChart3 className="h-3 w-3" />
                  Analytics
                </button>
              ) : null}
              {!isShellEditor ? (
                <button
                  onClick={() => setSidebarTab("seo")}
                  className={`rounded-md px-2 py-1 text-xs transition-colors flex items-center gap-1 ${sidebarTab === "seo" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
                >
                  <Search className="h-3 w-3" /> SEO
                </button>
              ) : null}
              <button
                onClick={() => setSidebarTab("copilot")}
                className={`rounded-md px-2 py-1 text-xs transition-colors ${sidebarTab === "copilot" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                Copilot
              </button>
              <button
                onClick={() => setSidebarTab("dynamic")}
                className={`rounded-md px-2 py-1 text-xs transition-colors flex items-center gap-1 ${sidebarTab === "dynamic" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                <Sparkles className="h-3 w-3" />
                Dynamic
              </button>
              <button
                onClick={() => setSidebarTab("comments")}
                className={`rounded-md px-2 py-1 text-xs transition-colors flex items-center gap-1 ${sidebarTab === "comments" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                <MessageSquare className="h-3 w-3" />
                Comments
              </button>
              {!isShellEditor ? (
                <button
                  onClick={() => setSidebarTab("versions")}
                  className={`rounded-md px-2 py-1 text-xs transition-colors flex items-center gap-1 ${sidebarTab === "versions" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
                >
                  <History className="h-3 w-3" />
                  Versions
                </button>
              ) : null}
            </div>
          ) : null}
          {showSidebar ? (
            <button
              type="button"
              onClick={() => {
                setSidebarTab("comments");
                setCommentMode((current) => !current);
              }}
              aria-pressed={commentMode}
              title={commentMode ? "Comment mode active" : "Enter comment mode"}
              className={`rounded-md border border-border px-3 py-1.5 text-sm transition-colors inline-flex items-center gap-1.5 ${
                commentMode
                  ? "bg-amber-500/15 text-amber-900 dark:text-amber-100"
                  : "text-muted-foreground hover:bg-accent"
              }`}
            >
              <Pin className="h-4 w-4" />
              {commentMode ? "Comment mode on" : "Comment mode"}
            </button>
          ) : null}
          {!isShellEditor ? (
            <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5">
              <button
                onClick={() => setEditorMode("simple")}
                className={`rounded-md px-2 py-1 text-xs transition-colors ${editorMode === "simple" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                Simple
              </button>
              <button
                onClick={() => setEditorMode("full")}
                className={`rounded-md px-2 py-1 text-xs transition-colors ${editorMode === "full" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-muted-foreground hover:bg-accent"}`}
              >
                Full controls
              </button>
            </div>
          ) : null}
          {!isSimpleEditor ? (
            <button
              type="button"
              onClick={() => {
                if (!showLayersPanel) {
                  setShowLayersPanel(true);
                  setIsLayersCollapsed(false);
                  return;
                }
                setShowLayersPanel(false);
              }}
              className={`rounded-md border border-border px-3 py-1.5 text-sm transition-colors ${
                showLayersPanel
                  ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "text-muted-foreground hover:bg-accent"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Layers3 className="h-3.5 w-3.5" />
                Layers
              </span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void handleRunThreeSecondTest()}
            disabled={isRunningThreeSecondTest}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isRunningThreeSecondTest ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ClipboardCheck className="h-3.5 w-3.5" />
            )}
            3-Second Test
          </button>
          <button
            type="button"
            onClick={() => setShowShortcutsOverlay(true)}
            className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-accent"
            title="Keyboard shortcuts (Cmd+/)"
          >
            <span className="inline-flex items-center gap-1.5">
              <Keyboard className="h-3.5 w-3.5" />
              Shortcuts
            </span>
          </button>
          {/* PB-T.18: Save section to library */}
          <div className="relative">
            <button
              onClick={() => setShowSectionLibrary(!showSectionLibrary)}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent"
              title={isSimpleEditor ? "Add a ready-made section" : "Browse templates and saved sections"}
            >
              {isSimpleEditor ? "Add section" : "Section Library"}
            </button>
            {showSectionLibrary && (
              <div className="absolute right-0 top-full mt-1 z-10 w-[360px] rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg p-3">
                <SectionPicker onSelect={handleInsertSection} />
              </div>
            )}
          </div>
          {!isShellEditor ? (
            <button
              type="button"
              onClick={() => void handleSaveAsTemplate()}
              disabled={saveTemplateState === "saving"}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              title="Save the current page as a tenant-private reusable template"
            >
              {saveTemplateState === "saving" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save as Template
            </button>
          ) : null}
          {!isSimpleEditor && selectedSectionId ? (
            <button
              type="button"
              onClick={handleDuplicateSelectedComponent}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-accent"
              title="Clone the selected block subtree directly below the original"
            >
              <ClipboardCheck className="h-3.5 w-3.5" />
              Clone Section
            </button>
          ) : null}
          {!isSimpleEditor && selectedRemoteEditors.length > 0 ? (
            <div
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-700"
              title={`Currently edited by ${selectedRemoteEditors.map((editor) => editor.displayName).join(", ")}`}
            >
              <Lock className="h-3.5 w-3.5" />
              {selectedRemoteEditors.length === 1
                ? `${selectedRemoteEditors[0]!.displayName} is editing this block`
                : `${selectedRemoteEditors.length} editors are on this block`}
            </div>
          ) : null}
          {!isSimpleEditor ? (
            <div className="relative">
              <button
                onClick={() => {
                  if (showSaveSection) {
                    setShowSaveSection(false);
                    return;
                  }
                  handleOpenSaveSectionDialog();
                }}
                disabled={!selectedSectionId}
                className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent flex items-center gap-1.5"
                title="Save the selected block subtree as a reusable section"
              >
                <Save className="h-3.5 w-3.5" />
                Save Section
              </button>
              {showSaveSection && (
                <div className="absolute right-0 top-full mt-1 z-10 w-64 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg p-3">
                  <h4 className="text-xs font-semibold text-[var(--color-foreground)] mb-2">
                    Save to section library
                  </h4>
                  <input
                    value={savedSectionName}
                    onChange={(event) => setSavedSectionName(event.target.value)}
                    placeholder={selectedSectionType ? `${selectedSectionType} section` : "Reusable section name"}
                    className="mb-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none"
                  />
                  <label className="mb-2 block text-[11px] font-medium text-[var(--color-muted-foreground)]">
                    Category
                    <select
                      value={savedSectionCategory}
                      onChange={(event) => setSavedSectionCategory(event.target.value as SavedSectionCategory)}
                      className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none"
                    >
                      {SAVED_SECTION_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>{category.label}</option>
                      ))}
                    </select>
                  </label>
                  <p className="mb-2 text-xs leading-5 text-[var(--color-muted-foreground)]">
                    This saves the selected block and its nested subtree as a tenant-scoped reusable section for the "My Sections" picker.
                  </p>
                  {saveSectionMessage ? (
                    <p className={`mb-2 text-xs ${saveSectionStatus === "error" ? "text-[var(--color-destructive)]" : "text-[var(--color-success)]"}`}>
                      {saveSectionMessage}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => void handleSaveSection()}
                    disabled={saveSectionStatus === "saving"}
                    className="w-full rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                  >
                    {saveSectionStatus === "saving" ? "Saving..." : "Save section"}
                  </button>
                </div>
              )}
            </div>
          ) : null}
          {/* C14: Pre-publish checklist */}
          {!isShellEditor ? (
            <div className="relative">
            <button
              onClick={handleToggleChecklist}
                className={`rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-1.5 ${checklistIssues.length > 0 ? "text-[var(--color-warning)]" : "text-[var(--color-success)]"}`}
                disabled={isRunningAccessibilityAudit}
              >
              {isRunningAccessibilityAudit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5" />}
              {isSimpleEditor
                ? checklistIssues.length > 0 ? `${checklistIssues.length} things to finish` : "Ready to go live"
                : checklistIssues.length > 0 ? `${checklistIssues.length} issues` : "Ready"}
            </button>
            {showChecklist && (
              <div className="absolute right-0 top-full mt-1 z-10 w-64 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg p-3">
                <h4 className="text-xs font-semibold text-[var(--color-foreground)] mb-2">
                  {isSimpleEditor ? "Before you go live" : "Pre-publish Check"}
                </h4>
                {checklistIssues.length === 0 ? (
                  <p className="text-xs text-[var(--color-success)]">
                    {isSimpleEditor ? "Everything looks ready to go live." : "All checks passed. Ready to publish."}
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {checklistIssues.map((issue) => (
                      <li key={issue} className="flex items-start gap-1.5 text-xs text-[var(--color-warning)]">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                )}
                {accessibilityAudit?.issues.length ? (
                  <div className="mt-3 space-y-1 border-t border-border pt-2">
                    <div className="text-[11px] font-medium text-foreground">Accessibility audit</div>
                    {accessibilityAudit.issues.slice(0, 4).map((issue) => (
                      <div key={`${issue.id}-${issue.targets[0] ?? "root"}`} className="text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{issue.help}</span>
                        {issue.impact ? ` (${issue.impact})` : ""}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            </div>
          ) : null}
          {!isShellEditor ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsSchedulePanelOpen((isOpen) => !isOpen)}
                aria-expanded={isSchedulePanelOpen}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-accent"
              >
                <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
                {page?.scheduledPublishAt ? "Scheduled" : "Schedule Publish"}
                {scheduledPublishCountdown ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    {scheduledPublishCountdown}
                  </span>
                ) : null}
              </button>

              {isSchedulePanelOpen ? (
                <div className="absolute right-0 top-full z-20 mt-2 w-[360px] rounded-2xl border border-border bg-card p-4 shadow-xl">
                  <div className="mb-3">
                    <p className="text-sm font-semibold text-foreground">Schedule this version</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Choose a go-live time at least 1 hour from now. We store the schedule in UTC and show it in your selected timezone.
                    </p>
                  </div>
                  {page?.scheduledPublishAt ? (
                    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <div className="font-semibold">Scheduled for {scheduledPublishLabel}</div>
                      {scheduledPublishCountdown ? <div className="mt-0.5">{scheduledPublishCountdown}</div> : null}
                    </div>
                  ) : null}
                  <div className="grid gap-3">
                    <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                      Publish date and time
                      <input
                        type="datetime-local"
                        value={scheduledPublishValue}
                        min={scheduledPublishMinimumValue}
                        onChange={(event) => setScheduledPublishValue(event.target.value)}
                        disabled={schedulePublishState === "saving" || schedulePublishState === "canceling"}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Scheduled publish time"
                      />
                    </label>
                    <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                      Timezone
                      <select
                        value={scheduledPublishTimezone}
                        onChange={(event) => setScheduledPublishTimezone(event.target.value)}
                        disabled={schedulePublishState === "saving" || schedulePublishState === "canceling"}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Scheduled publish timezone"
                      >
                        {scheduleTimeZoneOptions.map((timeZone) => (
                          <option key={timeZone} value={timeZone}>{timeZone}</option>
                        ))}
                      </select>
                    </label>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <div className="text-xs text-muted-foreground">
                        {previewVersion ? `Scheduling v${previewVersion.versionNumber}` : "Latest saved version will be used"}
                      </div>
                      <div className="flex items-center gap-2">
                        {page?.scheduledPublishAt ? (
                          <button
                            type="button"
                            onClick={() => void handleCancelScheduledPublish()}
                            disabled={schedulePublishState === "saving" || schedulePublishState === "canceling"}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {schedulePublishState === "canceling" ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                            Cancel schedule
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => void handleSchedulePublish()}
                          disabled={schedulePublishState === "saving" || schedulePublishState === "canceling"}
                          className="inline-flex items-center gap-1 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {schedulePublishState === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock3 className="h-3 w-3" />}
                          {page?.scheduledPublishAt ? "Reschedule" : "Schedule"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            onClick={handlePublish}
            className="btn-press rounded-md px-4 py-1.5 text-sm font-medium text-white"
            style={{ background: "var(--gradient-energy)" }}
          >
            {isShellEditor ? "Save and Republish" : isSimpleEditor ? "Make it live" : "Publish"}
          </button>
        </div>
      </div>

      <StylePanelProvider
        data={data}
        onDataChange={handleDataChange}
        editorBreakpoint={previewDevice}
        onSelectionChange={({ id, type }) => {
          setSelectedSectionId(id);
          setSelectedSectionType(type);
          setSavedSectionCategory(inferSavedSectionCategory(type));
          setSectionContextMenu(null);
        }}
      >
        {/* DSS-26: Inject hover/focus/active CSS for all Puck components */}
        <StyleStateInjector
          content={(data.content ?? []) as Array<{ type?: string; props?: Record<string, unknown> }>}
        />

        <div className="flex h-[calc(100vh-48px)]">
          {showLayersPanel ? (
            <div className={`${isLayersCollapsed ? "w-12" : "w-[300px]"} overflow-hidden transition-[width] duration-200`}>
              <LayerNavigatorPanel
                remoteEditorsByComponent={remoteEditorsByComponent}
                isCollapsed={isLayersCollapsed}
                onCollapseToggle={() => setIsLayersCollapsed((current) => !current)}
              />
            </div>
          ) : null}
          <div className="relative flex flex-1 justify-center overflow-hidden bg-[var(--color-surface)]">
            {shellError || shellSaveMessage || builderError || previewPublishMessage || publishActionMessage || themeSaveMessage || collabConflict ? (
              <div className="pointer-events-none absolute left-4 top-16 z-10 max-w-xl space-y-2">
                {shellError ? (
                  <div className="pointer-events-auto flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{shellError}</span>
                  </div>
                ) : null}
                {builderError ? (
                  <div className="pointer-events-auto flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{builderError}</span>
                  </div>
                ) : null}
                {previewPublishMessage ? (
                  <div className={`pointer-events-auto rounded-2xl px-4 py-3 text-sm ${
                    previewPublishState === "error"
                      ? "border border-destructive/25 bg-destructive/5 text-destructive"
                      : "border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-foreground"
                  }`}>
                    {previewPublishMessage}
                  </div>
                ) : null}
                {publishActionMessage ? (
                  <div className={`pointer-events-auto rounded-2xl px-4 py-3 text-sm ${
                    publishActionTone === "error"
                      ? "border border-destructive/25 bg-destructive/5 text-destructive"
                      : "border border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  }`}>
                    {publishActionMessage}
                  </div>
                ) : null}
                {themeSaveMessage ? (
                  <div className={`pointer-events-auto rounded-2xl px-4 py-3 text-sm ${
                    themeSaveState === "error"
                      ? "border border-destructive/25 bg-destructive/5 text-destructive"
                      : "border border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  }`}>
                    {themeSaveMessage}
                  </div>
                ) : null}
                <ConflictToast
                  conflict={collabConflict}
                  onDismiss={() => setCollabConflict(null)}
                />
                {shellSaveMessage ? (
                  <div className={`pointer-events-auto rounded-2xl px-4 py-3 text-sm ${
                    shellSaveStatus === "error"
                      ? "border border-destructive/25 bg-destructive/5 text-destructive"
                      : "border border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  }`}>
                    {shellSaveMessage}
                  </div>
                ) : null}
              </div>
            ) : null}
            {isSimpleEditor ? (
              <div className="pointer-events-none absolute left-4 top-16 z-10 max-w-xl">
                <div className="pointer-events-auto rounded-[24px] border border-border bg-background/95 px-5 py-4 shadow-lg backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Guided continuation</p>
                  <h2 className="mt-2 text-lg font-semibold text-foreground">{continuationTitle}</h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{continuationDescription}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setShowSectionLibrary(true)}
                      className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Add a section
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode("full")}
                      className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Open full controls
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            <EditorHeatmapContext.Provider value={{ showHeatmap, lookup: heatmapLookup }}>
              <EditorDynamicPreviewContext.Provider value={{ visitorContext: DYNAMIC_PREVIEW_CONTEXTS[dynamicPreviewPreset] ?? DYNAMIC_PREVIEW_CONTEXTS.default }}>
              <EditorCommentPreviewContext.Provider
                value={{
                  isCommentMode: commentMode,
                  commentPins: openCommentPins,
                  pendingTarget: pendingCommentTarget
                    ? {
                        componentId: pendingCommentTarget.componentId,
                        x: pendingCommentTarget.position.x,
                        y: pendingCommentTarget.position.y,
                      }
                    : null,
                  onCommentTarget: handleCommentTarget,
                }}
              >
              <EditorCollabPreviewContext.Provider value={{ collaborators, sendCursor }}>
              {!isSimpleEditor ? (
                <div className="pointer-events-auto absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-1 rounded-full border border-border bg-background/95 px-2 py-1 shadow-lg backdrop-blur">
                  <span className="px-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Canvas
                  </span>
                  {([
                    { device: "desktop" as const, icon: Monitor, label: "Desktop canvas" },
                    { device: "tablet" as const, icon: Tablet, label: "Tablet canvas" },
                    { device: "mobile" as const, icon: Smartphone, label: "Mobile canvas" },
                  ]).map(({ device, icon: Icon, label }) => (
                    <button
                      key={device}
                      type="button"
                      onClick={() => setPreviewDevice(device)}
                      className={`rounded-full p-1.5 transition-colors ${previewDevice === device ? "bg-[var(--color-primary)] text-white shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}
                      title={label}
                      aria-label={label}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              ) : null}
              <div
                ref={canvasRef}
                onContextMenu={handleCanvasContextMenu}
                style={{
                  ...editorCanvasThemeStyle,
                  width: previewWidth,
                  maxWidth: "100%",
                  transition: "width 300ms ease",
                  boxShadow: previewDevice === "desktop"
                    ? undefined
                    : "0 0 0 2px var(--color-primary)",
                }}
                className={`relative bg-[var(--color-background)] ${editorThemeScopeClass}`}
                data-site-theme-preset={activeThemePresetId ?? undefined}
                data-editor-breakpoint={previewDevice}
              >
              <style>{editorThemeScopeCss}</style>
              {sectionContextMenu ? (
                <>
                  <button
                    type="button"
                    aria-label="Close section context menu"
                    className="fixed inset-0 z-30 cursor-default bg-transparent"
                    onClick={() => setSectionContextMenu(null)}
                  />
                  <div
                    role="menu"
                    aria-label="Section actions"
                    className="fixed z-40 w-56 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-xl"
                    style={{ left: sectionContextMenu.x, top: sectionContextMenu.y }}
                  >
                    <div className="px-2.5 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        Section actions
                      </p>
                      <p className="mt-1 truncate text-xs text-foreground">
                        {sectionContextMenu.componentType ?? sectionContextMenu.componentId}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition hover:bg-accent"
                      onClick={() => handleOpenSaveSectionDialog(sectionContextMenu.componentType)}
                    >
                      <Save className="h-3.5 w-3.5" />
                      Save Section
                    </button>
                  </div>
                </>
              ) : null}
              {previewVersion ? (
                <div className="pointer-events-auto absolute left-4 top-4 z-20 max-w-xl rounded-[20px] border border-[var(--color-primary)]/20 bg-background/95 px-4 py-3 shadow-lg backdrop-blur">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs font-semibold text-[var(--color-primary)]">
                      Previewing v{previewVersion.versionNumber}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {previewVersion.generationSource === "autosave" ? "Autosaved checkpoint" : previewVersion.generationSource}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    This canvas is showing a historical snapshot. Exit preview to edit the live draft, or restore this version to create a new latest checkpoint.
                  </p>
                  <button
                    type="button"
                    onClick={handleExitVersionPreview}
                    className="mt-3 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                  >
                    Return to live draft
                  </button>
                </div>
              ) : null}
              {!isShellEditor && currentPageId ? (
                <CollabCursors
                  canvasRef={canvasRef}
                  collaborators={collaborators}
                  currentUserId={currentUser?.userId}
                  observedVersion={editorData}
                />
              ) : null}
              {showHeatmap && heatmap.length > 0 ? (
                <div className="pointer-events-none absolute right-4 top-4 z-20 w-52 rounded-2xl border border-border/70 bg-background/90 p-3 shadow-lg backdrop-blur">
                  <div className="mb-2 flex items-center justify-between text-[11px] font-medium text-foreground">
                    <span>Attention Map</span>
                    <span className="text-muted-foreground">{heatmap.length} sections</span>
                  </div>
                  <div className="space-y-1.5">
                    {heatmap.map((item) => (
                      <div
                        key={`${item.componentType}-${item.componentIndex}`}
                        className={`rounded-lg border px-2 py-1 text-[10px] ${heatmapTone(item.attention)}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate pr-2">{item.componentType} #{item.componentIndex + 1}</span>
                          <span className="font-mono">{Math.round(item.attention * 100)}%</span>
                        </div>
                        {item.reasons[0] ? (
                          <div className="mt-1 text-[9px] text-muted-foreground">
                            {item.reasons[0]}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <Puck
                config={filteredPuckConfig}
                data={editorData}
                onPublish={handlePublish}
                onChange={previewVersion ? () => undefined : handleDataChange}
              />
              {isSimpleEditor ? (
                <SimplifiedEditor
                  data={data}
                  onDataChange={handleDataChange}
                  onOpenSectionLibrary={() => setShowSectionLibrary(true)}
                  onOpenFullControls={() => setEditorMode("full")}
                />
              ) : null}
              </div>
              </EditorCollabPreviewContext.Provider>
              </EditorCommentPreviewContext.Provider>
              </EditorDynamicPreviewContext.Provider>
            </EditorHeatmapContext.Provider>
          </div>
          {showSidebar ? (
          <div className={`${isInspectorCollapsed ? "w-12" : "w-[320px]"} border-l border-border bg-card transition-[width] duration-200 ${isInspectorCollapsed ? "overflow-hidden" : "overflow-y-auto"}`}>
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              {isInspectorCollapsed ? null : (
                <div>
                  <div className="text-sm font-semibold text-foreground">Inspector</div>
                  <div className="text-[11px] text-muted-foreground">Style, content, SEO, automation</div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsInspectorCollapsed((current) => !current)}
                className="rounded-md p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                title={isInspectorCollapsed ? "Expand inspector" : "Collapse inspector"}
                aria-label={isInspectorCollapsed ? "Expand inspector" : "Collapse inspector"}
              >
                {isInspectorCollapsed ? <PanelRightOpen className="h-4 w-4" /> : <PanelRightClose className="h-4 w-4" />}
              </button>
            </div>
            {isInspectorCollapsed ? (
              <div className="flex h-full flex-col items-center gap-3 py-3">
                <div className="rounded-lg bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
                  {sidebarTab === "seo" ? <Search className="h-4 w-4" /> : null}
                  {sidebarTab === "analytics" ? <BarChart3 className="h-4 w-4" /> : null}
                  {sidebarTab === "style" ? <Layers3 className="h-4 w-4" /> : null}
                  {sidebarTab === "copilot" || sidebarTab === "dynamic" ? <Sparkles className="h-4 w-4" /> : null}
                  {sidebarTab === "comments" ? <MessageSquare className="h-4 w-4" /> : null}
                  {sidebarTab === "versions" ? <History className="h-4 w-4" /> : null}
                </div>
                <div className="rotate-180 [writing-mode:vertical-rl] text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {sidebarTab}
                </div>
              </div>
            ) : (
              <>
            {sidebarTab === "seo" && !isShellEditor ? (
              <SeoEditorPanel
                meta={seoMeta}
                onMetaChange={setSeoMeta}
                components={editorComponents}
                onComponentsChange={handleSeoComponentsChange}
              />
            ) : null}

            {sidebarTab === "style" ? <StylePanel /> : null}

            {sidebarTab === "analytics" && !isShellEditor ? (
              <PageAnalyticsPanel pageId={currentPageId} />
            ) : null}

            {sidebarTab === "copilot" ? (
              <div>
                <StylePromptGenerator />
                <div className="border-b border-border bg-muted/20 p-3">
                  <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Style System
                  </div>
                  <ConsistencyChecker
                    components={consistencyComponents}
                    onFix={handleConsistencyFix}
                    onSelectComponent={setSelectedSectionId}
                  />
                </div>
                <DesignCopilot
                  pageId={currentPageId}
                  analysis={optimizationAnalysis}
                  components={editorComponents}
                  seo={seoMeta}
                  onApplyResult={handleCopilotApplyResult}
                  showHeatmap={showHeatmap}
                  onHeatmapChange={setShowHeatmap}
                />
              </div>
            ) : null}

            {sidebarTab === "dynamic" ? (
              <DynamicContentPanel
                previewPreset={dynamicPreviewPreset}
                onPreviewPresetChange={setDynamicPreviewPreset}
              />
            ) : null}

            {sidebarTab === "comments" ? (
              <PageCommentPanel
                pageId={currentPageId}
                pageVersionId={page?.currentVersionId ?? null}
                comments={pageComments}
                isLoading={isLoadingComments}
                error={pageCommentsError}
                createComment={handleCreatePageComment}
                markResolved={handleResolvePageComment}
                replyToComment={handleReplyToPageComment}
                onSelectComment={handleFocusPageComment}
                commentMode={commentMode}
                onCommentModeChange={setCommentMode}
              />
            ) : null}

            {sidebarTab === "versions" && !isShellEditor ? (
              <VersionHistoryPanel
                pageId={currentPageId}
                currentVersionId={page?.currentVersionId ?? null}
                publishedVersionId={page?.publishedVersionId ?? null}
                onRestore={handleVersionRestore}
                onPreview={handleVersionPreview}
              />
            ) : null}
              </>
            )}
          </div>
          ) : null}
        </div>
        <ThreeSecondTestPanel
          isOpen={showThreeSecondTestPanel}
          isLoading={isRunningThreeSecondTest}
          error={threeSecondTestError}
          input={threeSecondTestInput}
          result={threeSecondTestResult}
          onClose={() => setShowThreeSecondTestPanel(false)}
          onRunAgain={() => void handleRunThreeSecondTest()}
        />
        <ShortcutsOverlay
          isOpen={showShortcutsOverlay}
          onClose={() => setShowShortcutsOverlay(false)}
        />
      </StylePanelProvider>
    </div>
  );
}
